/**
 * Usage tab: product analytics for the operator (features/usage_analytics.md).
 * What users do in the app — which strategies they look at, whether they use
 * the signal chart, which tools and panels get used — over a date range.
 *
 * One filter row scopes everything below it. Every number shown in a chart is
 * also printed beside it, so the hover tooltips never gate a value.
 * Rendered inside ManagementDashboard's main area and reuses its card styles.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "@/api/client";
import { rolesLabel } from "@/api/capabilities";
import type { UsageReport, UsageTimeline } from "@/api/types";
import { useAppSelector } from "@/state/hooks";
import dash from "./ManagementDashboard.module.css";
import styles from "./UsageView.module.css";

const PRESETS = [7, 30, 90] as const;

const PANEL_LABEL: Record<string, string> = {
  journal: "Trade journal",
  notes: "Notes",
  news: "News",
  help: "Help",
  weekly_behaviour: "Weekly behaviour",
  one_click: "One-click trading",
  trading: "Trading panel",
  objects_tree: "Objects tree",
  chart_colors: "Chart colors",
  fib_levels: "Fib levels",
  terminal: "Terminal",
};

const TOOL_LABEL: Record<string, string> = {
  horizontal: "Horizontal line",
  vertical: "Vertical line",
  diagonal: "Trend line",
  path: "Path",
  fibonacci: "Fibonacci",
  long: "Long position",
  short: "Short position",
  priceRange: "Price range",
  text: "Text",
};

const INDICATOR_LABEL: Record<string, string> = {
  ma: "Moving average",
  rsi: "RSI",
  anchor_rsi: "Anchored RSI",
};

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function rangeFor(days: number): { start: string; end: string } {
  const end = new Date();
  const start = new Date(end.getTime() - (days - 1) * 86_400_000);
  return { start: isoDay(start), end: isoDay(end) };
}

function fmt(n: number): string {
  if (n >= 10_000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

function pct(part: number, whole: number): string {
  return whole ? `${Math.round((part / whole) * 100)}%` : "—";
}

function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

function humanEvent(name: string): string {
  const s = name.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function ago(iso: string | null): string {
  if (!iso) return "—";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 60) return `${Math.max(mins, 0)}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / (60 * 24))}d ago`;
}

/** A horizontal bar with its value printed at the tip (never color-only). */
function Bar({ value, max, title }: { value: number; max: number; title: string }) {
  const w = max > 0 ? Math.max((value / max) * 100, value > 0 ? 2 : 0) : 0;
  return (
    <span className={styles.barCell} title={title}>
      <span className={styles.barTrack}>
        <span className={styles.barFill} style={{ width: `${w}%` }} />
      </span>
      <span className={styles.barVal}>{fmt(value)}</span>
    </span>
  );
}

/** Daily active users: one series, so no legend — the card title names it.
 *  Crosshair snaps to the nearest day; the tooltip adds that day's sessions
 *  and active minutes (read-outs, not a second axis). */
function TrendChart({ daily }: { daily: UsageReport["daily"] }) {
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(e.contentRect.width, 240)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const H = 180;
  const pad = { l: 34, r: 12, t: 12, b: 24 };
  const n = daily.length;
  const maxV = Math.max(1, ...daily.map((d) => d.active_users));
  const step = maxV <= 4 ? 1 : Math.ceil(maxV / 4);
  const top = step * Math.ceil(maxV / step);
  const ticks = Array.from({ length: Math.floor(top / step) + 1 }, (_, i) => i * step);
  const x = (i: number) => pad.l + (n <= 1 ? 0 : (i / (n - 1)) * (width - pad.l - pad.r));
  const y = (v: number) => pad.t + (1 - v / top) * (H - pad.t - pad.b);
  const line = daily.map((d, i) => `${i ? "L" : "M"}${x(i)},${y(d.active_users)}`).join("");
  const area = n ? `${line}L${x(n - 1)},${y(0)}L${x(0)},${y(0)}Z` : "";
  const labelIdx = n <= 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1];

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - r.left + pad.l;
    const i = n <= 1 ? 0 : Math.round(((px - pad.l) / (width - pad.l - pad.r)) * (n - 1));
    setHover(Math.min(Math.max(i, 0), n - 1));
  };

  const h = hover !== null ? daily[hover] : null;
  const last = daily[n - 1];

  return (
    <div ref={wrap} className={styles.trend}>
      <svg width={width} height={H} role="img" aria-label="Daily active users">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={width - pad.r} y1={y(t)} y2={y(t)} className={styles.gridline} />
            <text x={pad.l - 8} y={y(t)} className={styles.axis} textAnchor="end" dominantBaseline="middle">
              {t}
            </text>
          </g>
        ))}
        {labelIdx.map((i) => (
          <text
            key={i}
            x={x(i)}
            y={H - 6}
            className={styles.axis}
            textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
          >
            {daily[i] ? shortDate(daily[i].date) : ""}
          </text>
        ))}
        <path d={area} className={styles.area} />
        <path d={line} className={styles.line} />
        {last ? (
          <circle cx={x(n - 1)} cy={y(last.active_users)} r={4} className={styles.dot} />
        ) : null}
        {h && hover !== null ? (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={H - pad.b} className={styles.cross} />
            <circle cx={x(hover)} cy={y(h.active_users)} r={4} className={styles.dot} />
          </g>
        ) : null}
        <rect
          x={pad.l}
          y={pad.t}
          width={Math.max(width - pad.l - pad.r, 1)}
          height={H - pad.t - pad.b}
          fill="transparent"
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        />
      </svg>
      {h && hover !== null ? (
        <div
          className={styles.tip}
          style={{ left: Math.min(Math.max(x(hover) - 70, 0), width - 150) }}
        >
          <div className={styles.tipDate}>{shortDate(h.date)}</div>
          <div>
            <strong>{h.active_users}</strong> active users
          </div>
          <div>
            <strong>{h.sessions}</strong> sessions
          </div>
          <div>
            <strong>{h.active_minutes}</strong> active minutes
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TopList({ title, rows, label }: { title: string; rows: { key: string; count: number }[]; label?: (k: string) => string }) {
  const max = Math.max(0, ...rows.map((r) => r.count));
  return (
    <div className={styles.topList}>
      <div className={styles.subhead}>{title}</div>
      {rows.length ? (
        rows.map((r) => (
          <div key={r.key} className={styles.topRow}>
            <span className={styles.topKey}>{label ? label(r.key) : r.key}</span>
            <Bar value={r.count} max={max} title={`${label ? label(r.key) : r.key}: ${r.count}`} />
          </div>
        ))
      ) : (
        <div className={styles.empty}>No changes yet</div>
      )}
    </div>
  );
}

function Timeline({ sub, start, end, onClose }: { sub: string; start: string; end: string; onClose: () => void }) {
  const [data, setData] = useState<UsageTimeline | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setError(null);
    api
      .adminUsageUser(sub, start, end)
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e instanceof ApiError ? e.message : "Couldn't load activity"));
    return () => {
      live = false;
    };
  }, [sub, start, end]);

  const days = (data?.days ?? []).filter((d) => d.events > 0).reverse();

  return (
    <section className={`${dash.card} ${styles.span2}`}>
      <div className={dash.cardHead}>
        <h2>{data ? data.name || data.email || sub : "Activity"}</h2>
        {data?.email && data.name ? <span className={dash.muted}>{data.email}</span> : null}
        {data?.opted_out ? <span className={styles.optedOut}>Opted out</span> : null}
        <button type="button" className={styles.linkBtn} onClick={onClose}>
          Close
        </button>
      </div>
      <div className={styles.timelineBody}>
        {error ? <div className={styles.empty}>{error}</div> : null}
        {!data && !error ? <div className={styles.empty}>Loading…</div> : null}
        {data && !days.length ? <div className={styles.empty}>No activity in this range</div> : null}
        {days.length ? (
          <div className={styles.timelineGrid}>
            <div>
              <div className={styles.subhead}>By day</div>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Day</th>
                    <th className={styles.r}>Active min</th>
                    <th>Most done</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => (
                    <tr key={d.date}>
                      <td>{shortDate(d.date)}</td>
                      <td className={`${styles.r} ${dash.mono}`}>{d.active_minutes}</td>
                      <td className={styles.wrapText}>
                        {d.top.map((t) => `${humanEvent(t.key)} ×${t.count}`).join(" · ") || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <div className={styles.subhead}>Latest events</div>
              <ul className={styles.events}>
                {data?.recent
                  .filter((e) => e.event !== "active_ping")
                  .slice(0, 40)
                  .map((e, i) => (
                    <li key={`${e.ts}-${i}`}>
                      <span className={`${styles.evTime} ${dash.mono}`}>
                        {new Date(e.ts).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                      <span>{humanEvent(e.event)}</span>
                      <span className={styles.dimText}>
                        {Object.entries(e.props)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(", ")}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function UsageView({ refreshTick }: { refreshTick: number }) {
  const [days, setDays] = useState<(typeof PRESETS)[number]>(30);
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const [data, setData] = useState<UsageReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [trendAsTable, setTrendAsTable] = useState(false);
  const strategyNames = useAppSelector((s) => s.strategies.strategies);

  // refreshTick (the top-bar refresh) re-anchors "today" and refetches.
  const { start, end } = useMemo(() => rangeFor(days), [days, refreshTick]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.adminUsage(start, end, includeAdmins));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't load usage");
    } finally {
      setLoading(false);
    }
  }, [start, end, includeAdmins]);

  useEffect(() => {
    void load();
  }, [load, refreshTick]);

  const nameOf = useCallback(
    (id: string) => strategyNames.find((s) => s.id === id)?.name || id,
    [strategyNames],
  );

  const d = data;
  const maxStratUsers = Math.max(0, ...(d?.strategies ?? []).map((s) => s.users));
  const maxTool = Math.max(0, ...(d?.tools ?? []).map((t) => t.selected));
  const maxPanel = Math.max(0, ...(d?.panels ?? []).map((p) => p.opens));
  const maxUserEvents = Math.max(0, ...(d?.users ?? []).map((u) => u.events));

  return (
    <div className={styles.wrap}>
      {/* One filter row scopes everything below. */}
      <div className={styles.filters}>
        <div className={dash.ftabs} style={{ marginLeft: 0 }}>
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              className={`${dash.ftab} ${days === p ? dash.ftabOn : ""}`}
              onClick={() => setDays(p)}
            >
              Last {p} days
            </button>
          ))}
        </div>
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={includeAdmins}
            onChange={(e) => setIncludeAdmins(e.target.checked)}
          />
          Include admins
        </label>
        {d ? (
          <span className={styles.coverage}>
            Based on {d.coverage.active_users} active of {d.coverage.registered_users} users
            {d.coverage.opted_out ? ` · ${d.coverage.opted_out} opted out` : ""}
            {d.coverage.notice_pending ? ` · ${d.coverage.notice_pending} not asked yet` : ""}
          </span>
        ) : null}
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}

      {d ? (
        <div className={loading ? styles.stale : undefined}>
          <div className={dash.stats} style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
            {[
              ["Active users", fmt(d.kpis.active_users)],
              ["Avg daily active", String(d.kpis.avg_daily_active)],
              ["Sessions", fmt(d.kpis.sessions)],
              ["Active min per user-day", String(d.kpis.avg_minutes_per_user_day)],
            ].map(([label, value]) => (
              <div key={label} className={dash.stat}>
                <div className={dash.statLbl}>{label}</div>
                <div className={dash.statVal}>{value}</div>
              </div>
            ))}
          </div>

          <div className={styles.grid}>
            <section className={`${dash.card} ${styles.span2}`}>
              <div className={dash.cardHead}>
                <h2>Daily active users</h2>
                <span className={dash.muted}>
                  {shortDate(d.range.measured_from)} – {shortDate(d.range.end)}
                  {d.range.measured_from > d.range.start ? " · tracking started " + shortDate(d.range.measured_from) : ""}
                </span>
                <div className={dash.ftabs}>
                  {(["Chart", "Table"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`${dash.ftab} ${(m === "Table") === trendAsTable ? dash.ftabOn : ""}`}
                      onClick={() => setTrendAsTable(m === "Table")}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              {trendAsTable ? (
                <div className={styles.trendTable}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th className={styles.r}>Active users</th>
                        <th className={styles.r}>Sessions</th>
                        <th className={styles.r}>Active minutes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...d.daily].reverse().map((r) => (
                        <tr key={r.date}>
                          <td>{shortDate(r.date)}</td>
                          <td className={`${styles.r} ${dash.mono}`}>{r.active_users}</td>
                          <td className={`${styles.r} ${dash.mono}`}>{r.sessions}</td>
                          <td className={`${styles.r} ${dash.mono}`}>{r.active_minutes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <TrendChart daily={d.daily} />
              )}
            </section>

            <section className={`${dash.card} ${styles.span2}`}>
              <div className={dash.cardHead}>
                <h2>Strategies</h2>
                <span className={dash.muted}>
                  {d.strategies_funnel.opened_tab} of {d.strategies_funnel.active_users} active users
                  opened the tab ({pct(d.strategies_funnel.opened_tab, d.strategies_funnel.active_users)}) ·{" "}
                  {d.strategies_funnel.selected} selected a strategy
                </span>
              </div>
              {d.strategies.length ? (
                <div className={styles.scrollX}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Strategy</th>
                        <th>Users</th>
                        <th className={styles.r}>Views</th>
                        <th className={styles.r}>Selected</th>
                        <th className={styles.r}>Focused</th>
                        <th className={styles.r}>Minutes on chart</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.strategies.map((s) => (
                        <tr key={s.strategy}>
                          <td className={styles.nameCell} title={s.strategy}>
                            {nameOf(s.strategy)}
                          </td>
                          <td className={styles.barCol}>
                            <Bar value={s.users} max={maxStratUsers} title={`${nameOf(s.strategy)}: ${s.users} users`} />
                          </td>
                          <td className={`${styles.r} ${dash.mono}`}>{s.views}</td>
                          <td className={`${styles.r} ${dash.mono}`}>{s.selections}</td>
                          <td className={`${styles.r} ${dash.mono}`}>{s.focus}</td>
                          <td className={`${styles.r} ${dash.mono}`}>{s.minutes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.empty}>No strategy views in this range</div>
              )}
            </section>

            <section className={dash.card}>
              <div className={dash.cardHead}>
                <h2>Signal chart</h2>
              </div>
              <div className={styles.miniStats}>
                {[
                  ["Users", d.signal.users],
                  ["Opens", d.signal.opens],
                  ["Came back (2+ days)", d.signal.returning_users],
                  ["Minutes viewed", d.signal.minutes],
                  ["Auto-opened for new users", d.signal.auto_opens],
                ].map(([label, value]) => (
                  <div key={label} className={styles.mini}>
                    <div className={styles.miniVal}>{value}</div>
                    <div className={styles.miniLbl}>{label}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className={dash.card}>
              <div className={dash.cardHead}>
                <h2>Panels</h2>
              </div>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Panel</th>
                    <th>Opens</th>
                    <th className={styles.r}>Users</th>
                  </tr>
                </thead>
                <tbody>
                  {d.panels.map((p) => (
                    <tr key={p.panel} className={p.opens ? undefined : styles.unused}>
                      <td>{PANEL_LABEL[p.panel] ?? p.panel}</td>
                      <td className={styles.barCol}>
                        <Bar value={p.opens} max={maxPanel} title={`${PANEL_LABEL[p.panel] ?? p.panel}: ${p.opens} opens`} />
                      </td>
                      <td className={`${styles.r} ${dash.mono}`}>{p.users}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className={`${dash.card} ${styles.span2}`}>
              <div className={dash.cardHead}>
                <h2>Drawing tools</h2>
                <span className={dash.muted}>picked → finished a drawing</span>
              </div>
              <div className={styles.scrollX}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Tool</th>
                      <th>Picked</th>
                      <th className={styles.r}>Drawn</th>
                      <th className={styles.r}>Finished</th>
                      <th className={styles.r}>Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.tools.map((t) => (
                      <tr key={t.tool} className={t.selected || t.created ? undefined : styles.unused}>
                        <td>{TOOL_LABEL[t.tool] ?? t.tool}</td>
                        <td className={styles.barCol}>
                          <Bar value={t.selected} max={maxTool} title={`${TOOL_LABEL[t.tool] ?? t.tool}: picked ${t.selected}×`} />
                        </td>
                        <td className={`${styles.r} ${dash.mono}`}>{t.created}</td>
                        <td className={`${styles.r} ${dash.mono}`}>{pct(Math.min(t.created, t.selected), t.selected)}</td>
                        <td className={`${styles.r} ${dash.mono}`}>{t.users}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className={`${dash.card} ${styles.span2}`}>
              <div className={dash.cardHead}>
                <h2>Charts</h2>
                <span className={dash.muted}>what users switch to</span>
              </div>
              <div className={styles.topGrid}>
                <TopList title="Symbols" rows={d.charts.symbols} />
                <TopList title="Timeframes" rows={d.charts.timeframes} />
                <TopList title="Chart types" rows={d.charts.chart_types} />
                <TopList title="Indicators added" rows={d.indicators} label={(k) => INDICATOR_LABEL[k] ?? k} />
              </div>
            </section>

            <section className={`${dash.card} ${styles.span2}`}>
              <div className={dash.cardHead}>
                <h2>Users</h2>
                <span className={dash.muted}>most recent first · click a user for their activity</span>
              </div>
              {d.users.length ? (
                <div className={styles.scrollX}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Events</th>
                        <th className={styles.r}>Active days</th>
                        <th className={styles.r}>Last seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.users.map((u) => (
                        <tr
                          key={u.sub}
                          className={`${styles.clickRow} ${selected === u.sub ? styles.rowOn : ""}`}
                          onClick={() => setSelected(selected === u.sub ? null : u.sub)}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") setSelected(selected === u.sub ? null : u.sub);
                          }}
                        >
                          <td className={styles.nameCell}>
                            {u.name || u.email || u.sub}
                            {u.name ? <span className={styles.dimText}> {u.email}</span> : null}
                            {u.opted_out ? (
                              <span className={`${styles.optedOut} ${styles.rowBadge}`}>Opted out</span>
                            ) : null}
                          </td>
                          <td className={styles.barCol}>
                            <Bar value={u.events} max={maxUserEvents} title={`${u.email}: ${u.events} events`} />
                          </td>
                          <td className={`${styles.r} ${dash.mono}`}>{u.active_days}</td>
                          <td className={`${styles.r} ${dash.mono}`}>{ago(u.last_seen)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.empty}>No activity in this range</div>
              )}
            </section>

            <section className={`${dash.card} ${styles.span2}`}>
              <div className={dash.cardHead}>
                <h2>Opted out of usage data</h2>
                <span className={dash.muted}>
                  {d.opted_out_users.length} of {d.coverage.registered_users} users · nothing above counts them
                </span>
              </div>
              {d.opted_out_users.length ? (
                <div className={styles.scrollX}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Role</th>
                        <th className={styles.r}>Turned off</th>
                        <th className={styles.r}>Events before</th>
                        <th className={styles.r}>Last active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.opted_out_users.map((u) => (
                        <tr
                          key={u.sub}
                          className={`${styles.clickRow} ${selected === u.sub ? styles.rowOn : ""}`}
                          onClick={() => setSelected(selected === u.sub ? null : u.sub)}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") setSelected(selected === u.sub ? null : u.sub);
                          }}
                        >
                          <td className={styles.nameCell}>
                            {u.name || u.email || u.sub}
                            {u.name ? <span className={styles.dimText}> {u.email}</span> : null}
                          </td>
                          <td>{rolesLabel(u.roles)}</td>
                          <td className={`${styles.r} ${dash.mono}`} title={u.since ?? ""}>
                            {ago(u.since)}
                          </td>
                          <td className={`${styles.r} ${dash.mono}`}>{fmt(u.events)}</td>
                          <td className={`${styles.r} ${dash.mono}`} title={u.last_active ?? ""}>
                            {ago(u.last_active)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.empty}>Nobody has turned usage data off</div>
              )}
            </section>

            <section className={`${dash.card} ${styles.span2}`}>
              <div className={dash.cardHead}>
                <h2>Not asked yet</h2>
                <span className={dash.muted}>
                  {d.notice_pending_users.length} of {d.coverage.registered_users} users · not in
                  the app since the notice went up, so they have never seen it
                </span>
              </div>
              {d.notice_pending_users.length ? (
                <div className={`${styles.scrollX} ${styles.scrollY}`}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Role</th>
                        <th className={styles.r}>Last active</th>
                        <th className={styles.r}>Events</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.notice_pending_users.map((u) => (
                        <tr key={u.sub}>
                          <td className={styles.nameCell}>
                            {u.name || u.email || u.sub}
                            {u.name ? <span className={styles.dimText}> {u.email}</span> : null}
                          </td>
                          <td>{rolesLabel(u.roles)}</td>
                          <td className={`${styles.r} ${dash.mono}`} title={u.last_active ?? ""}>
                            {ago(u.last_active)}
                          </td>
                          <td className={`${styles.r} ${dash.mono}`}>{fmt(u.events)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.empty}>Everyone has been shown the notice</div>
              )}
            </section>

            {selected ? (
              <Timeline sub={selected} start={start} end={end} onClose={() => setSelected(null)} />
            ) : null}
          </div>
        </div>
      ) : !error ? (
        <div className={styles.empty}>Loading…</div>
      ) : null}
    </div>
  );
}
